// ─── PublicationModule ────────────────────────────────────────────────────────
// Reusable "Latest Publication" module for a listing.
//   • Desktop: rendered in the sidebar (below Venue Enquiry).  source="listing_sidebar"
//   • Mobile:  rendered inline in the main content.            source="listing_mobile_inline"
// Feed = the listing's OWN published issue + any published issue where the listing is
// in featured_venue_ids, newest first. One issue → single card; many → manual slider
// (arrows + dots, no auto-rotation). Clicking opens the flip reader in a modal without
// leaving the page. Renders NOTHING when the listing has no published issue.
//
// Tracking: impression (once, when a card is shown), open (on modal open), plus the
// reader's own view / page-turn / download, all tagged with `source`.
import { useState, useEffect, useRef, useCallback, Suspense, lazy, Component } from 'react';
import { createPortal } from 'react-dom';
import { fetchListingPublications } from '../../services/magazineIssuesService';
import { trackPublicationEvent } from '../../services/publicationsAnalyticsService';
import { imgUrl } from '../../utils/imgUrl';

const PublicationsReaderPage = lazy(() => import('../../pages/PublicationsReaderPage.jsx'));

const FD = 'var(--font-heading-primary)';
const FB = 'var(--font-body)';
const COVER_RATIO = '2480 / 3508'; // A4 portrait, matches the rendered cover

// Contains any crash in the (heavy, lazy) reader so it can never blank the listing.
class ReaderBoundary extends Component {
  constructor(p) { super(p); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch() { /* contained, the listing stays intact */ }
  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#080706', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'rgba(255,255,255,0.75)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
        <div>The publication couldn’t be opened.</div>
        <button onClick={this.props.onClose} style={{ border: '1px solid rgba(255,255,255,0.4)', background: 'none', color: '#fff', padding: '9px 22px', borderRadius: 2, cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: 12, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Close</button>
      </div>
    );
  }
}

export default function PublicationModule({ listingId, listingSlug, listingName, source = 'listing_sidebar', C = {} }) {
  const border    = C.border    || 'rgba(0,0,0,0.10)';
  const surface   = C.surface   || '#ffffff';
  const text       = C.text      || '#1a1712';
  const textMuted = C.textMuted || '#8a8073';
  const gold       = C.gold      || '#c8a24a';

  const [pubs, setPubs] = useState(null);   // null = loading, [] = none, [...] = issues
  const [idx, setIdx]   = useState(0);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dir, setDir] = useState('next'); // slide direction for the page-turn transition
  const impressedRef = useRef(false);
  const touchX = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!listingSlug && !listingId) { setPubs([]); return; }
    fetchListingPublications(listingId, listingSlug)
      .then(list => { if (!cancelled) setPubs(Array.isArray(list) ? list : []); })
      .catch(() => { if (!cancelled) setPubs([]); });
    return () => { cancelled = true; };
  }, [listingId, listingSlug]);

  // Impression, fire once when the first card becomes available
  useEffect(() => {
    if (pubs && pubs.length && !impressedRef.current) {
      impressedRef.current = true;
      trackPublicationEvent(pubs[0].id, 'impression', source);
    }
  }, [pubs, source]);

  // Body scroll lock + Esc close while modal open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [open]);

  const active = pubs && pubs[idx];

  const openReader = useCallback(() => {
    if (!active) return;
    trackPublicationEvent(active.id, 'open', source);
    setOpen(true);
  }, [active, source]);

  if (pubs === null || pubs.length === 0) return null; // loading or none → render nothing

  const many = pubs.length > 1;
  const title = active.title || listingName || 'Publication';

  const atStart = idx === 0;
  const atEnd   = idx === pubs.length - 1;
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <div style={{ padding: 0 }}>
      {/* Section title with a gold rule (matches the design language) + understated counter */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: FB, fontSize: 11, color: gold, letterSpacing: '2.5px', textTransform: 'uppercase', fontWeight: 700 }}>
            Featured in LWD Magazine
          </span>
          {many && (
            <span style={{ fontFamily: FB, fontSize: 10, color: textMuted, letterSpacing: '2px', fontVariantNumeric: 'tabular-nums' }}>
              {pad(idx + 1)}&nbsp;/&nbsp;{pad(pubs.length)}
            </span>
          )}
        </div>
        <div style={{ width: 40, height: 2, background: gold, marginTop: 10, borderRadius: 1 }} />
      </div>

      {/* Page-turn transition, direction-aware fade + slight horizontal drift */}
      <style>{`
        @keyframes pubCoverNext { from { opacity: 0; transform: translateX(18px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes pubCoverPrev { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>

      {/* Cover, one at a time. Hover lifts it and (when many) fades the arrows in.
          Mobile: swipe. No dots, no peeking, no loop. */}
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onTouchStart={many ? (e => { touchX.current = e.touches[0].clientX; }) : undefined}
        onTouchEnd={many ? (e => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current; touchX.current = null;
          if (dx < -40 && !atEnd)   { setDir('next'); setIdx(idx + 1); }
          else if (dx > 40 && !atStart) { setDir('prev'); setIdx(idx - 1); }
        }) : undefined}
        style={{ position: 'relative' }}
      >
        <button
          onClick={openReader}
          aria-label={`Read the publication: ${title}`}
          style={{ display: 'block', width: '100%', padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
        >
          <div style={{ position: 'relative', width: '100%', aspectRatio: COVER_RATIO, overflow: 'hidden', borderRadius: 3, background: '#0f0d0b',
            boxShadow: hovered ? '0 26px 64px rgba(0,0,0,0.34)' : '0 18px 48px rgba(0,0,0,0.28)',
            transform: hovered ? 'translateY(-2px)' : 'none',
            transition: 'transform 0.35s ease, box-shadow 0.35s ease' }}>
            {active.cover_image && (
              <img key={active.id} src={imgUrl(active.cover_image, { width: 800, resize: 'contain' })} alt={`${title}, cover`} loading="lazy"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block',
                  animation: `${dir === 'prev' ? 'pubCoverPrev' : 'pubCoverNext'} 0.55s cubic-bezier(0.33, 0, 0.2, 1) both` }} />
            )}
          </div>
        </button>

        {/* Arrows, fade in on hover (desktop); disabled at the ends (no loop) */}
        {many && (
          <>
            <button onClick={() => { if (!atStart) { setDir('prev'); setIdx(idx - 1); } }} disabled={atStart} aria-label="Previous publication"
              style={{ position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%',
                border: 'none', background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 18, lineHeight: 1, cursor: atStart ? 'default' : 'pointer',
                opacity: hovered ? (atStart ? 0.2 : 0.9) : 0, transition: 'opacity 0.3s ease', pointerEvents: hovered ? 'auto' : 'none' }}>‹</button>
            <button onClick={() => { if (!atEnd) { setDir('next'); setIdx(idx + 1); } }} disabled={atEnd} aria-label="Next publication"
              style={{ position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%',
                border: 'none', background: 'rgba(0,0,0,0.45)', color: '#fff', fontSize: 18, lineHeight: 1, cursor: atEnd ? 'default' : 'pointer',
                opacity: hovered ? (atEnd ? 0.2 : 0.9) : 0, transition: 'opacity 0.3s ease', pointerEvents: hovered ? 'auto' : 'none' }}>›</button>
          </>
        )}
      </div>

      {/* Context + action, no repeated title; the cover already names the venue */}
      <div style={{ fontFamily: FB, fontSize: 13, color: textMuted, fontStyle: 'italic', lineHeight: 1.5, marginTop: 16 }}>
        An exclusive editorial feature.
      </div>
      <button onClick={openReader}
        style={{ marginTop: 12, border: 'none', background: 'none', padding: 0, cursor: 'pointer',
          fontFamily: FB, fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: gold }}>
        Read the publication →
      </button>

      {/* Modal reader, the embed reader renders in its NATIVE fixed-fullscreen mode
          (identical to the standalone /publications page), lifted above the listing
          (zIndex 4000) with the external link hidden. Close button sits above it. */}
      {open && active && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 4000 }}>
          <ReaderBoundary onClose={() => setOpen(false)}>
            <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: '#080706', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.6)', fontFamily: FB, fontSize: 13 }}>Opening…</div>}>
              <PublicationsReaderPage slug={active.slug} onBack={() => setOpen(false)} embedded />
            </Suspense>
          </ReaderBoundary>
        </div>,
        document.body
      )}
    </div>
  );
}
